// --- src/components/Navbar.tsx ---
import React from 'react';
import { AppView, Permissions, Company } from './types';
import {
  LayoutDashboard,
  Mail,
  Upload,
  ScanBarcode,
  History,
  Settings,
  Coins,
  Sun,
  Moon,
  User,
  Printer,
  ShoppingCart,
  Pause,
} from 'lucide-react';
import { SyncStatusCompact } from './ui';

interface NavbarProps {
  currentView: AppView;
  setView: (view: AppView) => void;
  isDarkMode: boolean;
  toggleDarkMode: () => void;
  printQueueCount?: number;
  permissions?: Permissions | null;
  isAdmin?: boolean;
  userProfile?: {
    role: 'admin' | 'employee';
    access_labels?: boolean;
    access_email?: boolean;
    access_import?: boolean;
    access_pos?: boolean;
    access_boss_dashboard?: boolean;
  } | null;
  companyBranding?: Company | null;
}

const Navbar: React.FC<NavbarProps> = ({
  currentView,
  setView,
  isDarkMode,
  toggleDarkMode,
  printQueueCount = 0,
  permissions,
  isAdmin = false,
  userProfile,
  companyBranding,
}) => {
  const displayName = companyBranding?.name || 'Solyom';
  const logoUrl = companyBranding?.logo_url;
  const hasCustomName = companyBranding?.name && companyBranding.name !== 'Solyom barkácsbolt';
  const navItems = [
    {
      id: AppView.DASHBOARD,
      label: 'Napi munkák',
      icon: LayoutDashboard,
    },
    {
      id: AppView.INVENTORY_SCAN,
      label: 'Leltározás',
      icon: ScanBarcode,
    },
    {
      id: AppView.DAILY_OPS,
      label: 'Készlet',
      icon: Coins,
      highlight: true, // Ez a kiemelt
    },
    {
      id: AppView.RECONCILIATION,
      label: 'Napló',
      icon: History,
    },
    {
      id: AppView.IMPORT_DATA,
      label: 'Adatbázis feltöltése',
      icon: Upload,
    },
    {
      id: AppView.EMAIL_GENERATOR,
      label: 'Email',
      icon: Mail,
    },
    {
      id: AppView.LABEL_PRINTER,
      label: 'Címkék',
      icon: Printer,
    },
    {
      id: AppView.POS_HISTORY,
      label: 'Kosár előzmények',
      icon: ShoppingCart,
    },
    {
      id: AppView.SUSPENDED_CARTS,
      label: 'Parkolt kosarak',
      icon: Pause,
    },
  ];

  return (
    <nav
      className="shadow-lg sticky top-0 z-50"
      style={{
        backgroundColor: 'var(--color-secondary, #1e293b)',
        color: 'var(--navbar-text-color, #ffffff)',
      }}
    >
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* LOGÓ / NÉV */}
          <div
            className="flex items-center gap-2 cursor-pointer"
            onClick={() => setView(AppView.DASHBOARD)}
          >
            {logoUrl ? (
              <img
                src={logoUrl}
                alt={displayName}
                className="h-10 w-10 rounded-lg object-contain bg-white p-1"
                onError={(e) => {
                  // Fallback to icon if image fails
                  e.currentTarget.style.display = 'none';
                  const iconContainer = e.currentTarget.nextElementSibling;
                  if (iconContainer) {
                    iconContainer.classList.remove('hidden');
                  }
                }}
              />
            ) : null}
            <div
              className={`p-1.5 rounded-lg ${logoUrl ? 'hidden' : ''}`}
              style={{ backgroundColor: 'var(--color-primary, #2563eb)' }}
            >
              <ScanBarcode size={24} className="text-white" />
            </div>
            <span className="text-xl font-bold tracking-tight hidden md:block">
              {displayName}
              {!hasCustomName && (
                <span className="ml-1" style={{ color: 'var(--color-secondary, #60a5fa)' }}>
                  barkácsbolt
                </span>
              )}
            </span>
          </div>

          {/* ASZTALI MENÜ */}
          <div className="hidden md:flex space-x-1">
            {navItems.map((item) => {
              // Permission checks - hide menu items if user doesn't have access

              // Import Data: Show if company has permission AND (user is admin OR has access_import)
              if (item.id === AppView.IMPORT_DATA) {
                if (!permissions?.hasImportModule) return null;
                if (!isAdmin && !userProfile?.access_import) return null;
              }

              // Labels: Show if company has permission AND (user is admin OR has access_labels)
              if (item.id === AppView.LABEL_PRINTER) {
                if (!permissions?.hasLabelModule) return null;
                if (!isAdmin && !userProfile?.access_labels) return null;
              }

              // Email: Show if company has permission AND (user is admin OR has access_email)
              if (item.id === AppView.EMAIL_GENERATOR) {
                if (!permissions?.hasEmailModule) return null;
                if (!isAdmin && !userProfile?.access_email) return null;
              }

              // POS History: Show if company has POS module AND (user is admin OR has access_pos)
              if (item.id === AppView.POS_HISTORY) {
                if (!permissions?.hasPosModule) return null;
                if (!isAdmin && !userProfile?.access_pos) return null;
              }

              // Suspended Carts: Show if company has POS module AND (user is admin OR has access_pos)
              if (item.id === AppView.SUSPENDED_CARTS) {
                if (!permissions?.hasPosModule) return null;
                if (!isAdmin && !userProfile?.access_pos) return null;
              }

              const Icon = item.icon;
              const isActive = currentView === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => setView(item.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 relative
                  ${
                    isActive
                      ? 'bg-primary text-white shadow-md transform scale-105'
                      : 'text-slate-300 dark:text-slate-400 hover:bg-slate-800 dark:hover:bg-gray-800 hover:text-white'
                  }`}
                >
                  <Icon size={18} />
                  {item.label}
                  {item.id === AppView.LABEL_PRINTER && printQueueCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                      {printQueueCount > 9 ? '9+' : printQueueCount}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* RIGHT SIDE: Theme Toggle + Profile + Settings */}
          <div className="flex items-center gap-2">
            {/* --- BOSS DASHBOARD BUTTON --- */}
            {permissions?.hasBossDashboard && (userProfile?.access_boss_dashboard || isAdmin) && (
              <button
                onClick={() => {
                  setView(AppView.BOSS_DASHBOARD);
                  window.location.hash = '#dashboard';
                }}
                className={`flex items-center gap-2 px-3 py-2 text-indigo-700 dark:text-indigo-300 transition-all bg-indigo-50 dark:bg-indigo-900/30 border border-indigo-200 dark:border-indigo-800 rounded-lg hover:bg-indigo-100 dark:hover:bg-indigo-900/50 hover:shadow-sm ${
                  currentView === AppView.BOSS_DASHBOARD
                    ? 'bg-indigo-100 dark:bg-indigo-900/50 shadow-sm'
                    : ''
                }`}
                title="Vezetői Statisztikák"
              >
                <LayoutDashboard size={20} />
                <span className="hidden md:inline font-semibold">Vezérlőpult</span>
              </button>
            )}

            {/* Sync Status Indicator (NEW) */}
            <div className="hidden md:block">
              <SyncStatusCompact />
            </div>

            {/* Theme Toggle Button */}
            <button
              onClick={toggleDarkMode}
              className="p-2 rounded-full transition-colors text-slate-300 dark:text-slate-400 hover:text-white hover:bg-slate-800 dark:hover:bg-gray-800"
              title={isDarkMode ? 'Világos mód' : 'Sötét mód'}
              aria-label={isDarkMode ? 'Világos mód' : 'Sötét mód'}
            >
              {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
            </button>

            {/* PROFIL GOMB */}
            <button
              onClick={() => setView(AppView.PROFILE)}
              className={`p-2 rounded-full transition-colors ${currentView === AppView.PROFILE ? 'bg-slate-800 dark:bg-gray-800 text-primary' : 'text-slate-400 dark:text-slate-500 hover:text-white hover:bg-slate-800 dark:hover:bg-gray-800'}`}
              title="Profil & Biztonság"
            >
              <User size={20} />
            </button>

            {/* BEÁLLÍTÁSOK GOMB */}
            <button
              onClick={() => setView(AppView.SETTINGS)}
              className={`p-2 rounded-full transition-colors ${currentView === AppView.SETTINGS ? 'bg-slate-800 dark:bg-gray-800 text-primary' : 'text-slate-400 dark:text-slate-500 hover:text-white hover:bg-slate-800 dark:hover:bg-gray-800'}`}
              title="Beállítások"
            >
              <Settings size={20} />
            </button>
          </div>
        </div>
      </div>

      {/* MOBIL MENÜ (Alsó sáv) */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-card border-t border-primary flex justify-around p-2 pb-4 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)] z-50">
        {navItems.map((item) => {
          // Permission checks - hide menu items if user doesn't have access

          // Import Data: Show if company has permission AND (user is admin OR has access_import)
          if (item.id === AppView.IMPORT_DATA) {
            if (!permissions?.hasImportModule) return null;
            if (!isAdmin && !userProfile?.access_import) return null;
          }

          // Labels: Show if company has permission AND (user is admin OR has access_labels)
          if (item.id === AppView.LABEL_PRINTER) {
            if (!permissions?.hasLabelModule) return null;
            if (!isAdmin && !userProfile?.access_labels) return null;
          }

          // Email: Show if company has permission AND (user is admin OR has access_email)
          if (item.id === AppView.EMAIL_GENERATOR) {
            if (!permissions?.hasEmailModule) return null;
            if (!isAdmin && !userProfile?.access_email) return null;
          }

          // POS History: Show if company has POS module AND (user is admin OR has access_pos)
          if (item.id === AppView.POS_HISTORY) {
            if (!permissions?.hasPosModule) return null;
            if (!isAdmin && !userProfile?.access_pos) return null;
          }

          // Suspended Carts: Show if company has POS module AND (user is admin OR has access_pos)
          if (item.id === AppView.SUSPENDED_CARTS) {
            if (!permissions?.hasPosModule) return null;
            if (!isAdmin && !userProfile?.access_pos) return null;
          }

          const Icon = item.icon;
          const isActive = currentView === item.id;
          return (
            <button
              key={item.id}
              onClick={() => setView(item.id)}
              className={`flex flex-col items-center justify-center w-full py-1 rounded-lg transition-colors relative
                ${isActive ? 'text-primary' : 'text-slate-400 dark:text-slate-500'}`}
            >
              <div className="relative">
                <Icon size={isActive ? 24 : 20} strokeWidth={isActive ? 2.5 : 2} />
                {item.id === AppView.LABEL_PRINTER && printQueueCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                    {printQueueCount > 9 ? '9+' : printQueueCount}
                  </span>
                )}
              </div>
              <span className="text-[10px] font-medium mt-1">
                {item.label.split('. ')[1] || item.label}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
};

export default Navbar;
